/**
 * IELTS band descriptors.
 *
 * Source: https://www.ielts.org/about-ielts/how-ielts-is-scored
 * The official names ("Expert User", "Modest User" …) are what test-takers
 * see on their results sheet, so showing them in our results screen makes
 * the score immediately meaningful.
 */

export interface BandDescriptor {
  /** The bucket's lower bound (inclusive). 7.0 means 7.0–7.49. */
  band: number
  /** Short title shown next to the score. */
  label: string
  /** One-sentence description for the results hero. */
  blurb: string
  /** Semantic tone — drives the colour treatment. */
  tone: 'expert' | 'strong' | 'good' | 'fair' | 'weak'
}

const DESCRIPTORS: BandDescriptor[] = [
  {
    band: 9,
    label: 'Expert',
    blurb: 'Fully operational command of English. Native-like fluency.',
    tone: 'expert',
  },
  {
    band: 8,
    label: 'Very Good',
    blurb:
      'Fully operational command with only occasional, unsystematic inaccuracies.',
    tone: 'expert',
  },
  {
    band: 7,
    label: 'Good',
    blurb:
      'Operational command — handles complex language well, with some inaccuracies.',
    tone: 'strong',
  },
  {
    band: 6,
    label: 'Competent',
    blurb:
      'Generally effective command despite some inaccuracies and misunderstandings.',
    tone: 'good',
  },
  {
    band: 5,
    label: 'Modest',
    blurb:
      'Partial command — likely makes mistakes but copes with the overall meaning.',
    tone: 'fair',
  },
  {
    band: 4,
    label: 'Limited',
    blurb:
      'Basic competence in familiar situations. Frequent problems with understanding.',
    tone: 'fair',
  },
  {
    band: 3,
    label: 'Extremely Limited',
    blurb:
      'Conveys and understands only general meaning in very familiar situations.',
    tone: 'weak',
  },
  {
    band: 2,
    label: 'Intermittent',
    blurb: 'Real difficulty understanding spoken and written English.',
    tone: 'weak',
  },
  {
    band: 1,
    label: 'Non-User',
    blurb: 'Essentially has no ability to use the language.',
    tone: 'weak',
  },
  {
    band: 0,
    label: 'No Attempt',
    blurb: 'No assessable information was provided.',
    tone: 'weak',
  },
]

/**
 * Pick the descriptor for a given band score. Half-bands round down to the
 * floor descriptor (8.5 → "Very Good", 6.5 → "Competent") which matches how
 * the IELTS official scale is grouped.
 */
export function getBandDescriptor(band: number): BandDescriptor {
  const floor = Math.floor(band)
  return (
    DESCRIPTORS.find((d) => d.band === floor) ??
    DESCRIPTORS[DESCRIPTORS.length - 1]!
  )
}

/**
 * Map a tone to a Tailwind text-color class. Centralising this keeps the
 * results screen from re-implementing band-to-colour logic in three places.
 */
export function toneTextClass(tone: BandDescriptor['tone']): string {
  switch (tone) {
    case 'expert':
      return 'text-brand-accent'
    case 'strong':
      return 'text-brand-primary'
    case 'good':
      return 'text-brand-primary'
    case 'fair':
      return 'text-brand-warning'
    case 'weak':
      return 'text-brand-error'
  }
}

export function toneFillClass(tone: BandDescriptor['tone']): string {
  switch (tone) {
    case 'expert':
      return 'bg-brand-accent'
    case 'strong':
      return 'bg-brand-primary'
    case 'good':
      return 'bg-brand-primary'
    case 'fair':
      return 'bg-brand-warning'
    case 'weak':
      return 'bg-brand-error'
  }
}
